import { Alert, AlertDescription } from '@crowdfpl/ui-web/alert';
import { Button } from '@crowdfpl/ui-web/button';
import { HCaptchaComponent } from '@crowdfpl/ui-web/hcaptcha';
import { HCaptchaErrorBoundary } from '@crowdfpl/ui-web/hcaptcha-error-boundary';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Mail, Loader2 } from 'lucide-react';
import React, { useEffect, useState, useRef } from 'react';
import { apiRequest } from '@/api/client';
import { AUTH_BASE_PATH } from '@/constants';
import { useAuth } from '@/hooks/auth';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import { useThemeDetection } from '@/hooks/utils/useThemeDetection';
import { notify } from '@/services/notifications';

interface EmailVerificationBannerProps {
  onDismiss?: () => void;
  className?: string;
}

export const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({
  className,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const { isAuthenticated, user, unverifiedEmail: storeUnverifiedEmail } = useAuth();
  const captchaRef = useRef<HCaptcha | null>(null);
  const theme = useThemeDetection();
  const isMobile = useIsMobile();

  // Use store value (reactive) with fallback to user email
  const unverifiedEmail = storeUnverifiedEmail || user?.email || '';

  const handleResendVerification = async () => {
    const emailToResend = storeUnverifiedEmail || user?.email;

    if (!emailToResend) {
      notify.error('Could not find an email address to resend verification. Please try registering again.');
      return;
    }

    setIsResending(true);
    try {
      const captchaToken = await captchaRef.current?.execute({ async: true });
      if (!captchaToken) {
        throw new Error('CAPTCHA challenge failed. Please try again.');
      }

      // Отправляем реальный запрос на API
      await apiRequest({
        url: `${AUTH_BASE_PATH}/resend-verification`,
        method: 'POST',
        data: { email: emailToResend, captcha: captchaToken },
      });

      notify.success('Verification email sent! Check your inbox.');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend verification email';
      notify.error(errorMessage);
    } finally {
      setIsResending(false);
      captchaRef.current?.resetCaptcha();
    }
  };

  // Авто-скрытие только когда email действительно верифицирован
  useEffect(() => {
    // Если email верифицирован, скрываем баннер
    if (isAuthenticated && user?.emailVerified) {
      setIsDismissed(true);
    }
  }, [isAuthenticated, user?.emailVerified]);

  // Show banner if user is unverified OR if there's an unverified email in store
  const shouldShow = !isDismissed && (
    (isAuthenticated && user && user.emailVerified === false) || !!storeUnverifiedEmail
  );
  if (!shouldShow) return null;

  return (
    <Alert
      className={`${className} bg-green-50 dark:bg-green-950/30 border-green-500/30`}
      variant="default"
    >
      <Mail className={isMobile ? 'h-3.5 w-3.5 text-green-600 dark:text-green-400' : 'h-4 w-4 text-green-600 dark:text-green-400'} />
      <AlertDescription className={isMobile ? 'flex flex-col gap-2 w-full' : 'flex items-center justify-between w-full'}>
        <div className="flex-1">
          <strong className={isMobile ? 'text-sm text-green-900 dark:text-green-100' : 'text-green-900 dark:text-green-100'}>Please verify your email address</strong>
          <p className={isMobile ? 'text-xs mt-0.5 text-green-800 dark:text-green-200' : 'text-sm mt-1 text-green-800 dark:text-green-200'}>
            We sent a verification link to <strong>{unverifiedEmail}</strong>.
            {isMobile ? ' ' : <br />}
            Click the link to verify your account.
          </p>
        </div>
        <div className={isMobile ? 'flex justify-end' : 'flex items-center gap-2'}>
          <Button
            variant="outline"
            size={isMobile ? 'sm' : 'sm'}
            onClick={handleResendVerification}
            disabled={isResending}
            className={`${isMobile ? 'h-8 text-xs' : ''} border-green-600 hover:bg-green-600 hover:text-white dark:border-green-400 dark:hover:bg-green-400 dark:hover:text-green-950`}
          >
            {isResending ? (
              <>
                <Loader2 className={isMobile ? 'mr-1.5 h-3 w-3 animate-spin' : 'mr-2 h-3 w-3 animate-spin'} />
                {isMobile ? 'Sending...' : 'Sending...'}
              </>
            ) : (
              'Resend Email'
            )}
          </Button>
        </div>
      </AlertDescription>
      <HCaptchaErrorBoundary>
        <HCaptchaComponent
          ref={captchaRef}
          onVerify={() => { }}
          onError={() => notify.error('CAPTCHA challenge failed. Please try again.')}
          theme={theme}
          size="invisible"
        />
      </HCaptchaErrorBoundary>
    </Alert>
  );
};

